import { useEffect, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { REDDIT_DEMO_ACCOUNTS, REDDIT_DEMO_BY_HANDLE, type DemoAccountKey } from '@perdiem/trpc/demo-accounts';
import { signInDemoAccount } from '../../lib/supabase';
import { setPendingMarketingAttribution } from '../../lib/marketing-attribution';
import { LoadingSpinner } from '../../components';

/** Tell Expo static export which /demo/[handle] pages to generate. */
export function generateStaticParams() {
  return REDDIT_DEMO_ACCOUNTS.map((account) => ({
    handle: account.redditHandle.toLowerCase(),
  }));
}

/**
 * /demo/[handle] — Reddit demo profile landing page.
 *
 * When a user clicks a Per Diem link in a Reddit thread, they land here.
 * The page shows a brief context modal explaining what Per Diem is and what
 * they're about to see. On dismiss, it signs into the demo account, navigates
 * to /(app), and replaces the URL so normal in-app navigation works.
 */
export default function RedditDemoPage() {
  const { handle } = useLocalSearchParams<{ handle: string }>();
  const router = useRouter();
  const [isLaunching, setIsLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedHandle = handle?.toLowerCase() ?? '';
  const redditAccount = REDDIT_DEMO_BY_HANDLE[normalizedHandle];
  const modalDescription =
    redditAccount && 'modalDescription' in redditAccount
      ? redditAccount.modalDescription
      : null;

  useEffect(() => {
    if (redditAccount) {
      void setPendingMarketingAttribution({
        source: 'reddit',
        pageType: 'demo',
        pageSlug: normalizedHandle,
      });
    }
  }, [normalizedHandle, redditAccount]);

  const handleLaunchDemo = async () => {
    if (!redditAccount || isLaunching) return;

    setIsLaunching(true);
    setError(null);

    try {
      await signInDemoAccount(redditAccount.key as DemoAccountKey);
      router.replace('/(app)');
    } catch {
      setError('Something went wrong. Please try again.');
      setIsLaunching(false);
    }
  };

  if (!redditAccount) {
    return (
      <View className="flex-1 items-center justify-center bg-stone-950 px-6">
        <Text className="text-xl font-semibold text-stone-50">Demo not found</Text>
        <Text className="mt-3 text-center text-base leading-6 text-stone-400">
          We couldn't find a demo profile for "{handle}".
        </Text>
        <Pressable
          onPress={() => router.replace('/')}
          className="mt-6 rounded-2xl bg-emerald-400 px-6 py-3"
        >
          <Text className="text-sm font-semibold text-stone-950">Go to Per Diem</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View className="flex-1 bg-stone-950">
      <Modal visible transparent animationType="fade">
        <Pressable
          className="flex-1 bg-black/80 px-4 py-8"
          onPress={handleLaunchDemo}
        >
          <View className="mx-auto w-full max-w-lg flex-1 justify-center">
            <Pressable
              className="rounded-[28px] border border-stone-800 bg-stone-950 p-6"
              onPress={(e) => e.stopPropagation()}
            >
              {/* Per Diem branding */}
              <Text className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
                Per Diem
              </Text>
              <Text className="mt-1 text-xs text-stone-500">
                FIRE Planning Platform
              </Text>

              {/* What they're about to see */}
              <Text className="mt-5 text-2xl font-semibold leading-8 text-stone-50">
                {redditAccount.title}
              </Text>
              <Text className="mt-3 text-base leading-7 text-stone-300">
                {redditAccount.summary}
              </Text>

              {/* Profile metadata */}
              <View className="mt-4 flex-row flex-wrap gap-2">
                <View className="rounded-full bg-stone-800 px-3 py-1">
                  <Text className="text-xs font-medium text-stone-300">
                    {redditAccount.ageLabel}
                  </Text>
                </View>
                <View className="rounded-full bg-stone-800 px-3 py-1">
                  <Text className="text-xs font-medium text-stone-300">
                    {redditAccount.household}
                  </Text>
                </View>
                <View className="rounded-full bg-stone-800 px-3 py-1">
                  <Text className="text-xs font-medium text-stone-300">
                    {redditAccount.location}
                  </Text>
                </View>
                <View className="rounded-full bg-stone-800 px-3 py-1">
                  <Text className="text-xs font-medium text-amber-400">
                    {redditAccount.redditSubreddit}
                  </Text>
                </View>
              </View>

              {/* Explanation */}
              <View className="mt-5 rounded-2xl bg-stone-900/60 p-4">
                <Text className="text-sm leading-6 text-stone-400">
                  {modalDescription ??
                    'We built a complete FIRE projection using the numbers from this post — accounts, income, budget, and retirement timeline. See when financial independence is realistic and what levers matter most.'}
                </Text>
              </View>

              {/* CTA */}
              <Pressable
                onPress={handleLaunchDemo}
                disabled={isLaunching}
                className={`mt-5 items-center rounded-2xl px-6 py-4 ${
                  isLaunching ? 'bg-stone-700' : 'bg-emerald-400'
                }`}
              >
                {isLaunching ? (
                  <LoadingSpinner fullScreen={false} />
                ) : (
                  <Text className="text-base font-semibold text-stone-950">
                    View the projection
                  </Text>
                )}
              </Pressable>

              {error && (
                <Text className="mt-3 text-center text-sm text-red-400">{error}</Text>
              )}

              {/* Secondary link */}
              <Pressable
                onPress={() => router.replace('/')}
                className="mt-3 items-center py-2"
              >
                <Text className="text-sm font-medium text-stone-500">
                  What is Per Diem?
                </Text>
              </Pressable>
            </Pressable>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}
